import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/Button';
import {
  BarChart3,
  RefreshCw,
  Download,
  Clock,
  TrendingUp,
  AlertTriangle,
  Package,
  Truck,
  Activity,
  Zap,
  Globe,
  Store,
  ShoppingCart,
  Users,
  CalendarDays,
  Trophy,
  Loader2,
} from 'lucide-react';
import { OverviewMetricCard } from '../components/reports/OverviewMetricCard';
import { TimeRangeSelector, TIME_RANGES, type TimeRange } from '../components/reports/TimeRangeSelector';
import { OverviewLineChart } from '../components/reports/OverviewLineChart';
import { OverviewBarChart } from '../components/reports/OverviewBarChart';
import { PerformanceMetrics } from '../components/reports/PerformanceMetrics';
import { useOverviewAnalytics } from '../hooks/useOverviewAnalytics';
import { useEnterpriseAnalytics } from '../hooks/useEnterpriseAnalytics';
import { ProfitAnalysis } from '../components/analytics/ProfitAnalysis';
import { ProductPerformance } from '../components/analytics/ProductPerformance';
import { ForecastingChart } from '../components/analytics/ForecastingChart';
import {
  exportAnalyticsToExcel,
  captureElementToPDF,
  exportAnalyticsOverviewToExcel,
  exportAnalyticsProfitToExcel,
  exportAnalyticsProductsToExcel,
  exportAnalyticsForecastingToExcel,
} from '../utils/analyticsExport';
import { getAdminId } from '../utils/getAdminId';

export const AnalyticsDashboard: React.FC = () => {
  const { user } = useAuth();
  const [timeRange, setTimeRange] = useState<TimeRange>(TIME_RANGES[2]);
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');
  const [comparisonEnabled, setComparisonEnabled] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'profit' | 'products' | 'forecasting'>('overview');
  const [mostSoldPeriod, setMostSoldPeriod] = useState<'today' | 'week' | 'month' | 'all'>('today');
  const [unsoldQtyFilter, setUnsoldQtyFilter] = useState<'all' | 'lt20' | '20-30' | 'gt30'>('all');

  const [refetchTrigger, setRefetchTrigger] = useState(0);

  const overviewRef = useRef<HTMLDivElement>(null);
  const profitRef = useRef<HTMLDivElement>(null);
  const productsRef = useRef<HTMLDivElement>(null);
  const forecastingRef = useRef<HTMLDivElement>(null);

  const handleCustomRangeChange = (start: string, end: string) => {
    setCustomStartDate(start);
    setCustomEndDate(end);
    setTimeRange({
      label: 'Custom',
      value: 'custom',
      days: 0,
      customStart: start,
      customEnd: end,
    });
    setRefetchTrigger((t) => t + 1);
  };

  const adminId = useMemo(() => {
    if (!user) return null;
    return getAdminId(user);
  }, [user]);

  const overviewAnalytics = useOverviewAnalytics(adminId, timeRange, comparisonEnabled);
  const enterpriseAnalytics = useEnterpriseAnalytics(adminId, timeRange);

  const handleRefresh = async () => {
    setRefreshing(true);
    overviewAnalytics.refetch();
    enterpriseAnalytics.refetch();
    setTimeout(() => setRefreshing(false), 800);
  };

  useEffect(() => {
    if (refetchTrigger > 0) {
      overviewAnalytics.refetch();
      enterpriseAnalytics.refetch();
    }
  }, [refetchTrigger]);


  const isLoading = overviewAnalytics.loading || enterpriseAnalytics.loading;

  const formatCurrency = (value: number) =>
    `€${value.toLocaleString('nl-NL', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

  const mostSoldItems = mostSoldPeriod === 'today'
    ? overviewAnalytics.sales.topSellingToday
    : mostSoldPeriod === 'week'
    ? overviewAnalytics.sales.topSellingWeek
    : mostSoldPeriod === 'month'
    ? overviewAnalytics.sales.topSellingMonth
    : overviewAnalytics.sales.topSellingAll;

  const { mostUnsoldItems, unsoldTotalCount } = useMemo(() => {
    const soldNames = new Set(
      mostSoldPeriod === 'today' ? overviewAnalytics.sales.soldNamesToday
      : mostSoldPeriod === 'week' ? overviewAnalytics.sales.soldNamesWeek
      : mostSoldPeriod === 'month' ? overviewAnalytics.sales.soldNamesMonth
      : overviewAnalytics.sales.soldNamesAll
    );
    const unsold = overviewAnalytics.sales.allProducts
      .filter(p => !soldNames.has(p.name))
      .sort((a, b) => a.quantity - b.quantity);
    const filtered = unsold.filter(item => {
      if (unsoldQtyFilter === 'lt20') return item.quantity < 20;
      if (unsoldQtyFilter === '20-30') return item.quantity >= 20 && item.quantity <= 30;
      if (unsoldQtyFilter === 'gt30') return item.quantity > 30;
      return true;
    });
    return { mostUnsoldItems: filtered.slice(0, 10), unsoldTotalCount: filtered.length };
  }, [overviewAnalytics.sales, mostSoldPeriod, unsoldQtyFilter]);

  const todayCustomers = overviewAnalytics.sales.dailyCustomers.find(
    d => d.date === new Date().toISOString().split('T')[0]
  );

  if (isLoading && !overviewAnalytics.lastUpdated) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[600px]">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-cyan-600 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg">
            <BarChart3 className="h-7 w-7 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              Enterprise Analytics Dashboard
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Comprehensive business intelligence and insights
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <TimeRangeSelector
            selectedRange={timeRange}
            onRangeChange={setTimeRange}
            onCustomRangeChange={handleCustomRangeChange}
            customStartDate={customStartDate}
            customEndDate={customEndDate}
            showComparison={true}
            comparisonEnabled={comparisonEnabled}
            onComparisonToggle={setComparisonEnabled}
          />
          <Button variant="secondary" onClick={handleRefresh} disabled={refreshing || isLoading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing || isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            variant="secondary"
            onClick={async () => { await exportAnalyticsToExcel(overviewAnalytics, enterpriseAnalytics, timeRange); }}
            disabled={isLoading}
          >
            <Download className="h-4 w-4 mr-2" />
            Export Excel
          </Button>
          <Button
            variant="secondary"
            onClick={async () => {
              const el = activeTab === 'overview' ? overviewRef.current : activeTab === 'profit' ? profitRef.current : activeTab === 'products' ? productsRef.current : forecastingRef.current;
              if (el) await captureElementToPDF(el, `analytics_${activeTab}_${timeRange.value}_${new Date().toISOString().split('T')[0]}.pdf`);
            }}
            disabled={isLoading}
          >
            <Download className="h-4 w-4 mr-2" />
            Export PDF
          </Button>
        </div>
      </div>

      {(overviewAnalytics.lastUpdated || enterpriseAnalytics.lastUpdated) && (
        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
          <Clock className="h-3 w-3" />
          Last updated: {(overviewAnalytics.lastUpdated || enterpriseAnalytics.lastUpdated)?.toLocaleTimeString()}
        </div>
      )}

      {(overviewAnalytics.error || enterpriseAnalytics.error) && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
          <p className="text-sm text-red-600 dark:text-red-400">
            {overviewAnalytics.error || enterpriseAnalytics.error}
          </p>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="flex items-center gap-2 border-b border-gray-200 dark:border-gray-700">
        {[
          { id: 'overview', label: 'Overview' },
          { id: 'profit', label: 'Profit Analysis' },
          { id: 'products', label: 'Products' },
          { id: 'forecasting', label: 'Forecasting' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-cyan-500 text-cyan-600 dark:text-cyan-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* OVERVIEW TAB */}
      {activeTab === 'overview' && (
        <div ref={overviewRef} className="space-y-6">
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={async () => { await exportAnalyticsOverviewToExcel(overviewAnalytics, timeRange); }}
              disabled={isLoading}
            >
              <Download className="h-4 w-4 mr-1" />
              Export Excel
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={async () => { if (overviewRef.current) await captureElementToPDF(overviewRef.current, `analytics_overview_${timeRange.value}_${new Date().toISOString().split('T')[0]}.pdf`); }}
              disabled={isLoading}
            >
              <Download className="h-4 w-4 mr-1" />
              Export PDF
            </Button>
          </div>
          {/* Row 1: Channel Revenue Breakdown */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <OverviewMetricCard
              title="Online Revenue"
              value={overviewAnalytics.sales.onlineRevenue}
              subtitle={`${overviewAnalytics.sales.onlineOrders} orders`}
              icon={Globe}
              gradient="from-blue-500 to-indigo-600"
              format="currency"
              loading={isLoading}
            />
            <OverviewMetricCard
              title="Shop Revenue"
              value={overviewAnalytics.sales.shopRevenue}
              subtitle={`${overviewAnalytics.sales.shopOrders} transactions`}
              icon={Store}
              gradient="from-emerald-500 to-green-600"
              format="currency"
              loading={isLoading}
            />
            <OverviewMetricCard
              title="Combined Revenue"
              value={overviewAnalytics.sales.totalRevenue}
              subtitle={`${overviewAnalytics.sales.totalOrders} total`}
              icon={TrendingUp}
              trend={{
                value: overviewAnalytics.sales.trends.revenue.changePercent,
                positive: overviewAnalytics.sales.trends.revenue.isPositive,
              }}
              gradient="from-violet-500 to-purple-600"
              format="currency"
              loading={isLoading}
            />
            <OverviewMetricCard
              title="Avg Order Value"
              value={overviewAnalytics.sales.avgOrderValue}
              subtitle={`${overviewAnalytics.sales.paidOrders} paid`}
              icon={ShoppingCart}
              gradient="from-cyan-500 to-teal-600"
              format="currency"
              loading={isLoading}
            />
          </div>

          {/* Row 2: Revenue by Period (Today / Week / Month) */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { label: 'Today', period: 'today' as const, icon: CalendarDays, rev: overviewAnalytics.sales.revToday, qty: overviewAnalytics.sales.qtyToday },
              { label: 'This Week', period: 'week' as const, icon: CalendarDays, rev: overviewAnalytics.sales.revWeek, qty: overviewAnalytics.sales.qtyWeek },
              { label: 'This Month', period: 'month' as const, icon: CalendarDays, rev: overviewAnalytics.sales.revMonth, qty: overviewAnalytics.sales.qtyMonth },
            ].map(({ label, period, icon: Icon, rev, qty }) => (
              <div key={period} className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Icon className="h-4 w-4 text-gray-400" />
                  <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">{label}</h4>
                </div>
                <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {formatCurrency(rev)}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {qty} items sold
                </p>
              </div>
            ))}
          </div>

          {/* Row 3: Customers Per Day */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
                <Users className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Customers Per Day
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Daily order/transaction count per channel
                </p>
              </div>
              {todayCustomers && (
                <div className="ml-auto flex gap-4 text-sm">
                  <span className="text-blue-600 dark:text-blue-400 font-semibold">
                    Today: {todayCustomers.onlineCount} online, {todayCustomers.shopCount} shop
                  </span>
                </div>
              )}
            </div>

            {overviewAnalytics.sales.dailyCustomers.length > 0 ? (
              <div className="overflow-x-auto">
                <OverviewBarChart
                  data={overviewAnalytics.sales.dailyCustomers.slice(-14).map(d => ({
                    label: new Date(d.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }),
                    value: d.onlineCount + d.shopCount,
                  }))}
                  title=""
                  height={200}
                />
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
                  <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <p className="text-xs text-gray-500 dark:text-gray-400">Online Total</p>
                    <p className="text-lg font-bold text-blue-600 dark:text-blue-400">
                      {overviewAnalytics.sales.dailyCustomers.reduce((s, d) => s + d.onlineCount, 0)}
                    </p>
                  </div>
                  <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <p className="text-xs text-gray-500 dark:text-gray-400">Shop Total</p>
                    <p className="text-lg font-bold text-green-600 dark:text-green-400">
                      {overviewAnalytics.sales.dailyCustomers.reduce((s, d) => s + d.shopCount, 0)}
                    </p>
                  </div>
                  <div className="text-center p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                    <p className="text-xs text-gray-500 dark:text-gray-400">Combined</p>
                    <p className="text-lg font-bold text-purple-600 dark:text-purple-400">
                      {overviewAnalytics.sales.dailyCustomers.reduce((s, d) => s + d.onlineCount + d.shopCount, 0)}
                    </p>
                  </div>
                  <div className="text-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <p className="text-xs text-gray-500 dark:text-gray-400">Avg/Day</p>
                    <p className="text-lg font-bold text-gray-700 dark:text-gray-300">
                      {overviewAnalytics.sales.dailyCustomers.length > 0
                        ? Math.round(overviewAnalytics.sales.dailyCustomers.reduce((s, d) => s + d.onlineCount + d.shopCount, 0) / overviewAnalytics.sales.dailyCustomers.length)
                        : 0}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-32 text-gray-500 dark:text-gray-400">
                No customer data for this period
              </div>
            )}
          </div>

          {/* Row 4: Most Sold Items */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-xl flex items-center justify-center">
                  <Trophy className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    Most Sold Items
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Top 10 by quantity sold
                  </p>
                </div>
              </div>
              <div className="flex gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                {[
                  { id: 'today' as const, label: 'Today' },
                  { id: 'week' as const, label: 'Week' },
                  { id: 'month' as const, label: 'Month' },
                  { id: 'all' as const, label: 'All Time' },
                ].map(p => (
                  <button
                    key={p.id}
                    onClick={() => setMostSoldPeriod(p.id)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                      mostSoldPeriod === p.id
                        ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow-sm'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            {mostSoldItems.length > 0 ? (
              <div className="space-y-2">
                {mostSoldItems.map((item, idx) => (
                  <div key={item.name} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs ${
                        idx === 0 ? 'bg-amber-500' : idx === 1 ? 'bg-gray-400' : idx === 2 ? 'bg-orange-700' : 'bg-gray-300 dark:bg-gray-600'
                      }`}>
                        {idx + 1}
                      </div>
                      <span className="font-medium text-gray-900 dark:text-gray-100 truncate max-w-[200px] sm:max-w-none">
                        {item.name}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-gray-500 dark:text-gray-400">{item.quantity} sold</span>
                      <span className="font-semibold text-gray-900 dark:text-gray-100">{formatCurrency(item.revenue)}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center h-32 text-gray-500 dark:text-gray-400">
                No sales data for this period
              </div>
            )}
          </div>

          {/* Row 5: Most Unsold Items */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-slate-100 dark:bg-slate-900/30 rounded-xl flex items-center justify-center">
                  <Package className="h-5 w-5 text-slate-600 dark:text-slate-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    Most Unsold Items
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Showing {mostUnsoldItems.length} of {unsoldTotalCount} unsold items
                  </p>
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                <div className="flex gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                  {[
                    { id: 'today' as const, label: 'Today' },
                    { id: 'week' as const, label: 'Week' },
                    { id: 'month' as const, label: 'Month' },
                    { id: 'all' as const, label: 'All Time' },
                  ].map(p => (
                    <button
                      key={p.id}
                      onClick={() => setMostSoldPeriod(p.id)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                        mostSoldPeriod === p.id
                          ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow-sm'
                          : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
                <div className="flex gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                  {[
                    { id: 'all' as const, label: 'All' },
                    { id: 'lt20' as const, label: '< 20' },
                    { id: '20-30' as const, label: '20–30' },
                    { id: 'gt30' as const, label: '> 30' },
                  ].map(q => (
                    <button
                      key={q.id}
                      onClick={() => setUnsoldQtyFilter(q.id)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                        unsoldQtyFilter === q.id
                          ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow-sm'
                          : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                      }`}
                    >
                      {q.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {mostUnsoldItems.length > 0 ? (
              <div className="space-y-2">
                {mostUnsoldItems.map((item, idx) => (
                  <div key={item.name} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs ${
                        idx === 0 ? 'bg-slate-500' : idx === 1 ? 'bg-slate-400' : idx === 2 ? 'bg-slate-300' : 'bg-gray-300 dark:bg-gray-600'
                      }`}>
                        {idx + 1}
                      </div>
                      <span className="font-medium text-gray-900 dark:text-gray-100 truncate max-w-[200px] sm:max-w-none">
                        {item.name}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-gray-500 dark:text-gray-400">{item.quantity} in stock</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center h-32 text-gray-500 dark:text-gray-400">
                All products have sales in this period
              </div>
            )}
          </div>

          {/* Revenue Trend + Stock Alerts + KPIs */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl flex items-center justify-center">
                    <TrendingUp className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Revenue Trend</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Combined online + shop performance</p>
                  </div>
                </div>
                {overviewAnalytics.sales.revenueTrend.length > 0 ? (
                  <OverviewLineChart data={overviewAnalytics.sales.revenueTrend} title="" color="blue" height={250} />
                ) : (
                  <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">
                    No revenue data available
                  </div>
                )}
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-xl flex items-center justify-center">
                    <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Stock Alerts</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Items requiring attention</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-amber-800 dark:text-amber-300">Low Stock</span>
                      <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                    </div>
                    <p className="text-3xl font-bold text-amber-700 dark:text-amber-300">
                      {overviewAnalytics.inventory.lowStockCount}
                    </p>
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">items below threshold</p>
                  </div>
                  <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-red-800 dark:text-red-300">Out of Stock</span>
                      <Package className="h-4 w-4 text-red-600 dark:text-red-400" />
                    </div>
                    <p className="text-3xl font-bold text-red-700 dark:text-red-300">
                      {overviewAnalytics.inventory.outOfStockCount}
                    </p>
                    <p className="text-xs text-red-600 dark:text-red-400 mt-1">items need reorder</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
                    <Activity className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Performance KPIs</h3>
                </div>
                <PerformanceMetrics
                  metrics={[
                    { label: 'Inventory Turnover', value: overviewAnalytics.performance.inventoryTurnover, unit: 'x', color: 'text-violet-500' },
                    { label: 'Fulfillment Rate', value: overviewAnalytics.performance.fulfillmentRate, unit: '%', color: 'text-blue-500' },
                  ]}
                  title=""
                />
              </div>

              <div className="bg-gradient-to-br from-cyan-500 to-teal-600 rounded-2xl p-6 text-white">
                <div className="flex items-center gap-3 mb-4">
                  <Zap className="h-6 w-6" />
                  <h3 className="text-lg font-semibold">Quick Stats</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-cyan-100">Categories</span>
                    <span className="font-bold">{overviewAnalytics.inventory.categories}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-cyan-100">Inventory Value</span>
                    <span className="font-bold">{formatCurrency(overviewAnalytics.inventory.totalValue)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-cyan-100">Total Stock</span>
                    <span className="font-bold">{overviewAnalytics.inventory.totalQuantity.toLocaleString()}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-cyan-100">Unique Products</span>
                    <span className="font-bold">{overviewAnalytics.inventory.totalItems.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'profit' && (
        <div ref={profitRef} className="space-y-6">
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={async () => { await exportAnalyticsProfitToExcel(enterpriseAnalytics.profit, timeRange); }}
              disabled={isLoading}
            >
              <Download className="h-4 w-4 mr-1" />
              Export Excel
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={async () => { if (profitRef.current) await captureElementToPDF(profitRef.current, `analytics_profit_${timeRange.value}_${new Date().toISOString().split('T')[0]}.pdf`); }}
              disabled={isLoading}
            >
              <Download className="h-4 w-4 mr-1" />
              Export PDF
            </Button>
          </div>
          <ProfitAnalysis
            profit={enterpriseAnalytics.profit}
            revenueTrend={overviewAnalytics.sales.revenueTrend}
            loading={isLoading}
          />
        </div>
      )}

      {activeTab === 'products' && (
        <div className="space-y-6">
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={async () => { await exportAnalyticsProductsToExcel(enterpriseAnalytics.products, timeRange); }}
              disabled={isLoading}
            >
              <Download className="h-4 w-4 mr-1" />
              Export Excel
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={async () => { if (productsRef.current) await captureElementToPDF(productsRef.current, `analytics_products_${timeRange.value}_${new Date().toISOString().split('T')[0]}.pdf`); }}
              disabled={isLoading}
            >
              <Download className="h-4 w-4 mr-1" />
              Export PDF
            </Button>
          </div>
          <ProductPerformance
            topProducts={enterpriseAnalytics.products.topProducts}
            bottomProducts={enterpriseAnalytics.products.bottomProducts}
            abcAnalysis={enterpriseAnalytics.products.abcAnalysis}
            loading={isLoading}
          />
        </div>
      )}

      {activeTab === 'forecasting' && (
        <div ref={forecastingRef} className="space-y-6">
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={async () => {
                await exportAnalyticsForecastingToExcel(
                  enterpriseAnalytics.forecasting,
                  timeRange,
                  overviewAnalytics.sales.revenueTrend
                );
              }}
              disabled={isLoading}
            >
              <Download className="h-4 w-4 mr-1" />
              Export Excel
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={async () => { if (forecastingRef.current) await captureElementToPDF(forecastingRef.current, `analytics_forecasting_${timeRange.value}_${new Date().toISOString().split('T')[0]}.pdf`); }}
              disabled={isLoading}
            >
              <Download className="h-4 w-4 mr-1" />
              Export PDF
            </Button>
          </div>
          <ForecastingChart
            forecast={enterpriseAnalytics.forecasting.revenueForecast}
            title="Revenue Forecast"
            historicalData={overviewAnalytics.sales.revenueTrend}
            loading={isLoading}
          />
        </div>
      )}
    </div>
  );
};

export default AnalyticsDashboard;
